import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Check, Search, X } from 'lucide-react';

import { apiGetAsUser, apiPostAsUser } from '../../lib/api_v2';
import { logError } from '../../lib/logger';
import { cn } from '../../lib/utils';

import { useListNavigation } from '../../hooks/useListNavigation';
import Flexicon from './Flexicon';

type LoadState = 'idle' | 'error' | 'loading' | 'ready';

export type PeoplePickerUser = {
  id: string;
  full_name: string;
  blurb?: string | null;
  direct_message_key?: string;
  flexicon?: unknown;
  google_contact?: boolean;
  ios_contact?: boolean;
  shared_chat_count?: number;
};

type SuggestedUsersResponse = {
  ok?: boolean;
  data?: {
    users?: PeoplePickerUser[];
  };
};

type ListNavigationHandle = {
  onKeyDown: (payload: { event: React.KeyboardEvent<HTMLInputElement> }) => boolean;
};

type CommonProps = {
  // Rendered between the search field and the list; unlike leadingContent it stays visible while searching.
  aboveListContent?: React.ReactNode;
  actionLabel?: string | null;
  actionUserId?: string | null;
  disabled?: boolean;
  emptySuggestedMessage?: string;
  externalErrorMessage?: string | null;
  getUserSubtitle?: (user: PeoplePickerUser) => React.ReactNode;
  leadingContent?: React.ReactNode;
  title?: string | null;
  workspaceId?: string | null;
};

type SingleSelectProps = CommonProps & {
  selectionMode?: 'single';
  onSelectUser: (user: PeoplePickerUser) => void;
  onToggleUser?: never;
  selectedUserIds?: never;
};

type MultiSelectProps = CommonProps & {
  selectionMode: 'multiple';
  onToggleUser: (user: PeoplePickerUser) => void;
  selectedUserIds: ReadonlySet<string>;
  onSelectUser?: never;
};

export type PeoplePickerPanelProps = SingleSelectProps | MultiSelectProps;

const EMPTY_USERS: readonly PeoplePickerUser[] = [];
const EMPTY_SELECTED_USER_IDS: ReadonlySet<string> = new Set();
const SEARCH_DEBOUNCE_MS = 250;
const MIN_SEARCH_QUERY_LENGTH = 3;

function phoneCountryHint() {
  try {
    return new Intl.Locale(window.navigator.language).region;
  } catch {
    return undefined;
  }
}

function getUserKey(user: PeoplePickerUser) {
  return user.id;
}

function defaultUserSubtitle(user: PeoplePickerUser) {
  return user.blurb;
}

export const SelectionIndicator = ({ checked }: { checked: boolean }) => {
  return (
    <span
      aria-hidden="true"
      className={cn(
        // transition-none so the check doesn't inherit a mid-transition color (black -> white flash)
        'flex size-5 items-center justify-center rounded-full border transition-none [&_*]:transition-none',
        checked ? 'border-idle-blue bg-idle-blue text-white' : 'border-gray-300 bg-transparent group-hover:border-gray-400',
      )}
    >
      {/* lucide's check glyph sits 0.25px high in its viewBox; half-pixel nudge recenters it optically */}
      {checked ? <Check size={12} strokeWidth={1.5} absoluteStrokeWidth className="translate-y-[0.5px]" /> : null}
    </span>
  );
};

type PeoplePickerRowProps = {
  actionLabel: string | null;
  disabled: boolean;
  isHighlighted: boolean;
  isMultiple: boolean;
  isSelected: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  onMouseMove: React.MouseEventHandler<HTMLButtonElement>;
  subtitle: React.ReactNode;
  user: PeoplePickerUser;
};

const PeoplePickerRow = React.forwardRef<HTMLButtonElement, PeoplePickerRowProps>(function PeoplePickerRow(
  { actionLabel, disabled, isHighlighted, isMultiple, isSelected, onClick, onMouseMove, subtitle, user },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      onMouseMove={onMouseMove}
      disabled={disabled}
      aria-pressed={isMultiple ? isSelected : undefined}
      className={cn(
        // -mx-4/-my-2 + px-4/py-2 bleed the highlight to the card edges and 8px past the
        // row vertically (per design) while keeping content position and 28px row rhythm.
        'group -mx-4 -my-2 flex min-h-16 cursor-pointer items-center gap-3 px-4 py-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-gray-400 disabled:cursor-wait disabled:opacity-60',
        // Keyboard nav (useListNavigation) is still live — without this the arrow-key
        // highlight is invisible and Enter selects a row the user can't see.
        isHighlighted && 'bg-gray-100',
      )}
    >
      <span className="inline-flex size-12 shrink-0">
        <Flexicon resource={user} size="size-12" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="truncate text-cursed leading-[18px] font-medium text-black">{user.full_name}</span>
        {subtitle ? <span className="truncate text-xs leading-3 text-gray-400">{subtitle}</span> : null}
      </span>
      {actionLabel ? <span className="shrink-0 text-[11px] font-medium text-gray-500">{actionLabel}</span> : null}
      {isMultiple ? (
        <span className="flex size-9 shrink-0 items-center justify-center">
          <SelectionIndicator checked={isSelected} />
        </span>
      ) : null}
    </button>
  );
});

const PeoplePickerPanel = ({
  aboveListContent = null,
  actionLabel = null,
  actionUserId = null,
  disabled = false,
  emptySuggestedMessage = 'No suggested people yet',
  externalErrorMessage = null,
  getUserSubtitle = defaultUserSubtitle,
  leadingContent = null,
  onSelectUser,
  onToggleUser,
  selectedUserIds = EMPTY_SELECTED_USER_IDS,
  selectionMode = 'single',
  title = 'Suggested',
  workspaceId = null,
}: PeoplePickerPanelProps) => {
  const [suggestedUsers, setSuggestedUsers] = useState<PeoplePickerUser[]>([]);
  const [searchResults, setSearchResults] = useState<PeoplePickerUser[]>([]);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null);
  const [isListScrolled, setIsListScrolled] = useState(false);
  const isMountedRef = useRef(false);
  const navigationRef = useRef<ListNavigationHandle | undefined>(undefined);
  const itemRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const trimmedQuery = searchQuery.trim();
  const debouncedTrimmedQuery = debouncedQuery.trim();
  const isSearchSettled = trimmedQuery === debouncedTrimmedQuery;
  const showSuggestedUsers = trimmedQuery.length === 0;
  // Short queries (below the server-side minimum) filter the loaded suggested list
  // client-side so results appear from the first keystroke.
  const isShortQuery = trimmedQuery.length > 0 && trimmedQuery.length < MIN_SEARCH_QUERY_LENGTH;
  const visibleUsers = useMemo(() => {
    if (showSuggestedUsers) return suggestedUsers;
    if (isShortQuery) {
      const loweredQuery = trimmedQuery.toLowerCase();
      return suggestedUsers.filter((user) => (user.full_name || '').toLowerCase().includes(loweredQuery));
    }
    return isSearchSettled ? searchResults : EMPTY_USERS;
  }, [isSearchSettled, isShortQuery, searchResults, showSuggestedUsers, suggestedUsers, trimmedQuery]);
  const isMultiple = selectionMode === 'multiple';

  const handleSelectUser = useCallback(
    function handlePeoplePickerSelection(user: PeoplePickerUser) {
      if (disabled) return;
      if (isMultiple) {
        onToggleUser?.(user);
        return;
      }

      onSelectUser?.(user);
    },
    [disabled, isMultiple, onSelectUser, onToggleUser],
  );

  const { selectedIndex, isKeyboardNavRef, createMouseEnterHandler, createClickHandler, onMouseLeave } = useListNavigation({
    items: visibleUsers,
    onSelect: handleSelectUser,
    ref: navigationRef,
    initialIndex: 0,
    getItemKey: getUserKey,
    missingSelectionFallbackIndex: 0,
  });

  const createRefHandler = useCallback(function createRefHandlerCallback(index: number) {
    return function setRef(element: HTMLButtonElement | null) {
      itemRefs.current[index] = element;
    };
  }, []);

  const handleSearchChange = useCallback(function handleSearchChangeCallback(event: React.ChangeEvent<HTMLInputElement>) {
    setSearchQuery(event.target.value);
  }, []);

  const handleClearSearch = useCallback(function handleClearSearchCallback() {
    setSearchQuery('');
    searchInputRef.current?.focus();
  }, []);

  const handleListScroll = useCallback(function handleListScrollCallback(event: React.UIEvent<HTMLDivElement>) {
    setIsListScrolled(event.currentTarget.scrollTop > 0);
  }, []);

  const handleSearchKeyDown = useCallback(function handleSearchKeyDownCallback(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown' && event.key !== 'Enter') return;

    const handled = navigationRef.current?.onKeyDown({ event });
    if (handled) event.preventDefault();
  }, []);

  useEffect(function markMounted() {
    isMountedRef.current = true;
    return function markUnmounted() {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(
    function debounceSearchQuery() {
      const timeout = window.setTimeout(() => {
        setDebouncedQuery(searchQuery.trim());
      }, SEARCH_DEBOUNCE_MS);

      return function clearSearchDebounce() {
        window.clearTimeout(timeout);
      };
    },
    [searchQuery],
  );

  useEffect(
    function loadUsersEffect() {
      const query = debouncedQuery.trim();

      if (query.length > 0 && query.length < MIN_SEARCH_QUERY_LENGTH) {
        // Short queries filter the already-loaded suggested list client-side; nothing to fetch.
        setSearchResults([]);
        setLoadState('ready');
        setLoadErrorMessage(null);
        return;
      }

      const controller = new AbortController();
      setLoadState('loading');
      setLoadErrorMessage(null);

      async function loadUsers() {
        try {
          const response = query
            ? await apiPostAsUser(
                '/suggested_users/search',
                { q: query, workspace_id: workspaceId || undefined, phone_country: phoneCountryHint() },
                { signal: controller.signal },
              )
            : await apiGetAsUser(
                workspaceId ? `/suggested_users?${new URLSearchParams({ workspace_id: workspaceId })}` : '/suggested_users',
                { signal: controller.signal },
              );
          const data = (await response.json()) as SuggestedUsersResponse;
          const loadedUsers = data.data?.users;

          if (!(response.ok && data.ok && Array.isArray(loadedUsers))) {
            throw new Error(`Failed to load users: ${response.status}`);
          }

          if (!isMountedRef.current) return;
          if (query) setSearchResults(loadedUsers);
          else setSuggestedUsers(loadedUsers);
          setLoadState('ready');
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') return;

          logError('PeoplePickerPanel', 'Failed to load users', error);
          if (!isMountedRef.current) return;
          if (query) setSearchResults([]);
          else setSuggestedUsers([]);
          setLoadState('error');
          setLoadErrorMessage('Could not load people. Please try again.');
        }
      }

      loadUsers();
      return function abortLoadUsers() {
        controller.abort();
      };
    },
    [debouncedQuery, workspaceId],
  );

  useEffect(
    function scrollSelectedUserIntoView() {
      if (!isKeyboardNavRef.current) return;
      isKeyboardNavRef.current = false;

      const selectedElement = itemRefs.current[selectedIndex];
      if (selectedElement) selectedElement.scrollIntoView({ behavior: 'instant', block: 'nearest' });
    },
    [isKeyboardNavRef, selectedIndex],
  );

  // Short queries render instantly from the suggested list; only empty/server queries load.
  const isLoading = !isShortQuery && (!isSearchSettled || loadState === 'loading');
  const isError = !isShortQuery && isSearchSettled && loadState === 'error';
  const showEmptyState = isShortQuery ? visibleUsers.length === 0 : isSearchSettled && loadState === 'ready' && visibleUsers.length === 0;
  const showUserRows = visibleUsers.length > 0;
  const showTitle = Boolean(title) && showSuggestedUsers;
  const showCard = showTitle || isLoading || isError || Boolean(externalErrorMessage) || showEmptyState || showUserRows;

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-gray-200">
      <div className="relative shrink-0">
        <div className="bg-gray-200 px-5 pt-5 pb-5">
          {/* label makes the entire bar (icon and padding included) click-to-focus the input */}
          <label className="group flex h-12 w-full cursor-text items-center gap-4 rounded-lg border-[0.5px] border-white bg-gray-50 px-4 hover:bg-white">
            <Search
              className={cn('size-4 shrink-0', searchQuery ? 'text-black' : 'text-gray-300 group-focus-within:text-black')}
              strokeWidth={1.5}
              absoluteStrokeWidth
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search by name, email, or phone"
              aria-label="Search people"
              autoFocus
              data-1p-ignore="true"
              className="min-w-0 flex-1 bg-transparent text-[15px] leading-5 font-normal text-black outline-none placeholder:text-gray-300"
            />
            {searchQuery ? (
              <button
                type="button"
                onClick={handleClearSearch}
                aria-label="Clear search"
                className="flex shrink-0 cursor-pointer items-center justify-center text-black focus:outline-none"
              >
                <X className="size-4" strokeWidth={1.5} absoluteStrokeWidth />
              </button>
            ) : null}
          </label>
        </div>

        {/* Pinned between the search bar and the scrollable list so it never scrolls away.
            Callers own this slot's spacing so collapsible content can animate to zero height. */}
        {aboveListContent ? <div className="px-5">{aboveListContent}</div> : null}

        {/* Sits exactly on the scroll clip line; divider-colored, fades in while the list is scrolled under it
            (same recipe as the workspace rail's toggle stroke). */}
        <div
          aria-hidden
          className={cn(
            'absolute inset-x-0 bottom-0 h-px bg-gray-300/60 transition-opacity duration-300 ease-fluid dark:bg-white/10',
            isListScrolled ? 'opacity-100' : 'opacity-0',
          )}
        />
      </div>

      <div onScroll={handleListScroll} className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
        {showSuggestedUsers && leadingContent ? <div className="pb-5">{leadingContent}</div> : null}

        {showCard ? (
          <div className="flex flex-col gap-4 rounded-lg border-[0.5px] border-white bg-gray-50 p-4">
            {showTitle ? (
              <>
                <h2 className="text-sm leading-5 font-medium text-black">{title}</h2>
                <div className="h-[0.5px] w-full bg-gray-200" />
              </>
            ) : null}

            {isLoading ? <div className="text-cursed leading-4 text-gray-500">Loading people</div> : null}
            {isError ? <div className="text-cursed leading-4 text-gray-500">{loadErrorMessage}</div> : null}
            {externalErrorMessage ? <div className="text-cursed leading-4 text-red-700">{externalErrorMessage}</div> : null}

            {showEmptyState ? (
              <div className="text-cursed leading-4 text-gray-500">
                {showSuggestedUsers ? emptySuggestedMessage : `No results for "${trimmedQuery}"`}
              </div>
            ) : null}

            {showUserRows ? (
              <div className="flex flex-col gap-7" onMouseLeave={onMouseLeave}>
                {visibleUsers.map((user, index) => (
                  <PeoplePickerRow
                    key={user.id}
                    ref={createRefHandler(index)}
                    user={user}
                    subtitle={getUserSubtitle(user)}
                    actionLabel={actionUserId === user.id ? actionLabel : null}
                    disabled={disabled}
                    isHighlighted={selectedIndex === index}
                    isMultiple={isMultiple}
                    isSelected={selectedUserIds.has(user.id)}
                    onClick={createClickHandler(index)}
                    onMouseMove={createMouseEnterHandler(index)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PeoplePickerPanel;
